import { DebouncedFunc, debounce } from 'lodash';
import React, { createContext, useCallback, useContext, useMemo } from 'react';
import useSaveExpDetailsTemplateMutation from './useSaveExpDetailsTemplateMutation';
import { ExperimentTemplateData } from '@experiment-details/types';

export const EXP_VIEW_AUTOSAVE_DELAY = 5000;

export type ExpDetailsTemplateApiContextType = {
  saveTemplateLocally: (template: ExperimentTemplateData) => void;
  saveTemplateOnServer: (template: ExperimentTemplateData) => void;
  saveTemplateOnServerDebounced: DebouncedFunc<
    (template: ExperimentTemplateData) => void
  >;
  cancelRequest: () => void;
  flushRequest: () => void;
  isLoading: boolean;
};

export const ExpDetailsTemplateApiContext = createContext<
  ExpDetailsTemplateApiContextType | undefined
>(undefined);

type DashboardApiProps = {
  children: React.ReactNode;
};
export const ExpDetailsTemplateApiProvider: React.FC<DashboardApiProps> = ({
  children
}) => {
  const saveExpDetailsTemplateMutation = useSaveExpDetailsTemplateMutation();
  const saveTemplateOnServerDebounced = useMemo(
    () =>
      debounce((templateData: ExperimentTemplateData) => {
        saveExpDetailsTemplateMutation.mutate({
          templateData
        });
      }, EXP_VIEW_AUTOSAVE_DELAY),
    []
  );
  const saveTemplateOnServer = useCallback(
    (templateData: ExperimentTemplateData) => {
      saveExpDetailsTemplateMutation.mutate({
        templateData
      });
    },
    []
  );

  const saveTemplateLocally = useCallback(
    (templateData: ExperimentTemplateData) => {
      saveExpDetailsTemplateMutation.mutate({
        templateData,
        onlyLocal: true
      });
    },
    []
  );

  const cancelRequest = useCallback(
    () => saveTemplateOnServerDebounced.cancel(),
    []
  );
  const flushRequest = useCallback(
    () => saveTemplateOnServerDebounced.flush(),
    []
  );

  return (
    <ExpDetailsTemplateApiContext.Provider
      value={{
        saveTemplateOnServerDebounced,
        saveTemplateOnServer,
        saveTemplateLocally,
        cancelRequest,
        flushRequest,
        isLoading: saveExpDetailsTemplateMutation.isLoading
      }}
    >
      {children}
    </ExpDetailsTemplateApiContext.Provider>
  );
};

export const useExpDetailsTemplateAPIContext = (): ExpDetailsTemplateApiContextType => {
  const ctx = useContext(ExpDetailsTemplateApiContext);

  if (ctx === undefined) {
    throw new Error(
      'useExpDetailsTemplateAPIContext must be used within a ExpDetailsTemplateApiProvider'
    );
  }

  return ctx;
};
